import { QueryFunctionContext, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MaintenanceRepresentation } from '@common/api';
import { QueryOptions } from '@util/query';
import { maintenanceApi } from '@data/rest/api';

type MaintenanceInfoKey = ['maintenance', string | undefined];

export const maintenanceKeys = Object.freeze({
    all: ['maintenance'],
    info(tenantId?: string) {
        return [...maintenanceKeys.all, tenantId] as MaintenanceInfoKey;
    },
});

function getMaintenanceInfo(context: QueryFunctionContext<MaintenanceInfoKey>) {
    const [, tenantId] = context.queryKey;

    if (!tenantId) {
        throw new Error('Invalid query, no tenant id');
    }

    return maintenanceApi.get(tenantId);
}

export function useMaintenanceInfo<TSelectData = MaintenanceRepresentation>(
    tenantId?: string | undefined,
    options?: QueryOptions<MaintenanceRepresentation, TSelectData, MaintenanceInfoKey, Response>
) {
    const key = maintenanceKeys.info(tenantId);
    const enabled = (options?.enabled ?? true) && tenantId !== undefined;
    return useQuery<MaintenanceRepresentation, Response, TSelectData, MaintenanceInfoKey>(key, getMaintenanceInfo, {
        ...options,
        enabled,
    });
}

type SetMaintenanceParams = { tenantId: string; value: boolean };

function setMaintenance(variables: SetMaintenanceParams) {
    return maintenanceApi.set(variables.tenantId, { maintenance: variables.value });
}

export function useSetMaintenance() {
    const queryClient = useQueryClient();

    return useMutation(setMaintenance, {
        onMutate: async (variables: SetMaintenanceParams) => {
            const key = maintenanceKeys.info(variables.tenantId);
            const oldInfo = queryClient.getQueryData<MaintenanceRepresentation | undefined>(key);

            queryClient.setQueryData<MaintenanceRepresentation>(key, (info) => {
                if (info) {
                    return { ...info, maintenance: variables.value };
                }
            });

            return { oldInfo };
        },
        onError(error, variables, context) {
            const key = maintenanceKeys.info(variables.tenantId);
            queryClient.setQueryData<MaintenanceRepresentation>(key, context?.oldInfo);
        },
        onSettled(data, error, variables) {
            const key = maintenanceKeys.info(variables.tenantId);
            return queryClient.invalidateQueries(key);
        },
    });
}
